/**
 * Snippet backlinks — reverse index of which docs embed which source blocks.
 *
 * Same persistence trick as doc organization: one reserved meta doc (tagged
 * `docs-meta`) holds the index so we don't need a backend schema change.
 *
 * Shape:
 *   linksBySource: {
 *     [sourceDocId]: {
 *       [sourceBlockId]: [{ consumerDocId, snippetBlockId }]
 *     }
 *   }
 */

import { SopAPI } from './api';
import { DOCS_META_TAG } from './docOrganization';

const flattenBlocks = (blocks, out = []) => {
  for (const b of blocks || []) {
    out.push(b);
    if (b.children?.length) flattenBlocks(b.children, out);
  }
  return out;
};

export const SNIPPET_BACKLINKS_ID = 'snippet-backlinks-meta';

const BODY_TYPE = 'snippet-backlinks';
export const SNIPPET_BACKLINKS_CHANGED = 'fokus:snippet-backlinks-changed';

export const createEmptyBacklinks = () => ({
  linksBySource: {},
});

export const parseSnippetBacklinks = (doc) => {
  const raw = Array.isArray(doc?.body)
    ? doc.body.find((entry) => entry && entry.type === BODY_TYPE)
    : null;
  if (!raw?.linksBySource || typeof raw.linksBySource !== 'object') {
    return createEmptyBacklinks();
  }
  return { linksBySource: { ...raw.linksBySource } };
};

export const serializeSnippetBacklinks = (index) => [
  {
    type: BODY_TYPE,
    version: 1,
    linksBySource: index.linksBySource || {},
  },
];

const sourceBlockIdsFromProps = (props = {}) => {
  if (Array.isArray(props.sourceBlockIds) && props.sourceBlockIds.length) {
    return props.sourceBlockIds.filter(Boolean);
  }
  if (props.sourceBlockId) return [props.sourceBlockId];
  return [];
};

/**
 * Collect outbound snippet embeds from a doc body.
 * Multi-block snippets expand to one backlink entry per source block
 * so every origin section gets the referenced mark.
 */
export const collectSnippetRefs = (body) => {
  const refs = [];
  for (const b of flattenBlocks(body || [])) {
    if (b.type !== 'snippet' || !b.props?.sourceDocId) continue;
    const sourceBlockIds = sourceBlockIdsFromProps(b.props);
    for (const sourceBlockId of sourceBlockIds) {
      refs.push({
        snippetBlockId: b.id,
        sourceDocId: b.props.sourceDocId,
        sourceBlockId,
      });
    }
  }
  return refs;
};

/**
 * Rewrite every link that points FROM `consumerDocId`, replacing them with
 * `refs` (the consumer's current snippet embeds).
 */
export const replaceConsumerLinks = (index, consumerDocId, refs) => {
  const linksBySource = {};

  // Keep links from every other consumer
  for (const [sourceDocId, byBlock] of Object.entries(index.linksBySource || {})) {
    const nextByBlock = {};
    for (const [sourceBlockId, entries] of Object.entries(byBlock || {})) {
      const kept = (entries || []).filter((e) => e.consumerDocId !== consumerDocId);
      if (kept.length) nextByBlock[sourceBlockId] = kept;
    }
    if (Object.keys(nextByBlock).length) linksBySource[sourceDocId] = nextByBlock;
  }

  // Add this consumer's current refs
  for (const ref of refs || []) {
    if (!ref.sourceDocId || !ref.sourceBlockId || !ref.snippetBlockId) continue;
    if (!linksBySource[ref.sourceDocId]) linksBySource[ref.sourceDocId] = {};
    if (!linksBySource[ref.sourceDocId][ref.sourceBlockId]) {
      linksBySource[ref.sourceDocId][ref.sourceBlockId] = [];
    }
    linksBySource[ref.sourceDocId][ref.sourceBlockId].push({
      consumerDocId,
      snippetBlockId: ref.snippetBlockId,
    });
  }

  return { linksBySource };
};

/** Drop all backlinks for source blocks (after the source is deleted). */
export const removeSourceBlockLinks = (index, sourceDocId, sourceBlockIds) => {
  if (!sourceDocId || !sourceBlockIds?.length) return index;
  const byBlock = { ...(index.linksBySource?.[sourceDocId] || {}) };
  let changed = false;
  for (const id of sourceBlockIds) {
    if (byBlock[id]) {
      delete byBlock[id];
      changed = true;
    }
  }
  if (!changed) return index;
  const linksBySource = { ...index.linksBySource };
  if (Object.keys(byBlock).length) linksBySource[sourceDocId] = byBlock;
  else delete linksBySource[sourceDocId];
  return { linksBySource };
};

export const getBacklinksForBlock = (index, sourceDocId, sourceBlockId) => {
  if (!sourceDocId || !sourceBlockId) return [];
  return index?.linksBySource?.[sourceDocId]?.[sourceBlockId] || [];
};

/** Map of blockId → backlink entries for one source doc (badge lookup). */
export const backlinksMapForDoc = (index, sourceDocId) => {
  const byBlock = index?.linksBySource?.[sourceDocId] || {};
  const map = {};
  for (const [blockId, entries] of Object.entries(byBlock)) {
    if (entries?.length) map[blockId] = entries;
  }
  return map;
};

const isNotFoundError = (e) => /not found|HTTP 404/i.test(e?.message || '');

let persistChain = Promise.resolve();
let recordExists = null; // null unknown, true/false after probe

const loadIndex = async () => {
  try {
    const doc = await SopAPI.get(SNIPPET_BACKLINKS_ID);
    recordExists = true;
    return parseSnippetBacklinks(doc);
  } catch (e) {
    if (isNotFoundError(e)) {
      recordExists = false;
      return createEmptyBacklinks();
    }
    throw e;
  }
};

const persistIndex = async (index) => {
  const payload = { body: serializeSnippetBacklinks(index) };
  if (recordExists) {
    try {
      await SopAPI.update(SNIPPET_BACKLINKS_ID, payload);
      return;
    } catch (e) {
      if (!isNotFoundError(e)) throw e;
      recordExists = false;
    }
  }
  await SopAPI.create({
    id: SNIPPET_BACKLINKS_ID,
    title: 'Snippet backlinks (system)',
    icon: '⧉',
    tags: [DOCS_META_TAG],
    ...payload,
  });
  recordExists = true;
};

const queueMutate = (updater) => {
  persistChain = persistChain
    .then(async () => {
      const current = await loadIndex();
      const next = updater(current);
      if (next === current) return;
      await persistIndex(next);
      window.dispatchEvent(
        new CustomEvent(SNIPPET_BACKLINKS_CHANGED, { detail: { index: next } })
      );
    })
    .catch((e) => console.error('Failed to save snippet backlinks:', e));
  return persistChain;
};

/**
 * After a consumer doc's body is saved — refresh that doc's contribution to
 * the reverse index. Fire-and-forget safe.
 */
export const syncSnippetBacklinksForDoc = (consumerDocId, body) => {
  if (!consumerDocId) return Promise.resolve();
  const refs = collectSnippetRefs(body);
  return queueMutate((index) => replaceConsumerLinks(index, consumerDocId, refs));
};

/** Clear reverse links when source blocks are deleted. */
export const clearSnippetBacklinksForSourceBlocks = (sourceDocId, sourceBlockIds) => {
  if (!sourceDocId || !sourceBlockIds?.length) return Promise.resolve();
  return queueMutate((index) =>
    removeSourceBlockLinks(index, sourceDocId, sourceBlockIds)
  );
};

/** One-shot load for hooks. */
export const fetchSnippetBacklinksIndex = loadIndex;
